import * as React from 'react'

import {
  DisplayScaleContext,
  type DisplayScale,
  type DisplayScaleContextValue,
} from '@/app/providers/display-scale-context'

const DISPLAY_SCALE_STORAGE_KEY = 'sensei-display-scale'
const DEFAULT_SCALE: DisplayScale = 'standard'

const SCALE_TO_FONT_SIZE: Record<DisplayScale, string> = {
  compact: '85%',
  standard: '90%',
  comfortable: '100%',
}

function isDisplayScale(value: unknown): value is DisplayScale {
  return value === 'compact' || value === 'standard' || value === 'comfortable'
}

function applyScaleToDocument(scale: DisplayScale) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.style.fontSize = SCALE_TO_FONT_SIZE[scale]
  root.dataset.displayScale = scale
}

function getInitialScale(): DisplayScale {
  if (typeof window === 'undefined') return DEFAULT_SCALE
  try {
    const stored = window.localStorage.getItem(DISPLAY_SCALE_STORAGE_KEY)
    if (isDisplayScale(stored)) {
      return stored
    }
  } catch {
    // storage erişilemezse varsayılan ölçek kullanılacak
  }
  return DEFAULT_SCALE
}

export function DisplayScaleProvider({ children }: { children: React.ReactNode }) {
  const [scale, setScaleState] = React.useState<DisplayScale>(() => getInitialScale())

  React.useEffect(() => {
    applyScaleToDocument(scale)
    if (typeof window === 'undefined') return
    try {
      if (scale === DEFAULT_SCALE) {
        window.localStorage.removeItem(DISPLAY_SCALE_STORAGE_KEY)
      } else {
        window.localStorage.setItem(DISPLAY_SCALE_STORAGE_KEY, scale)
      }
    } catch {
      // storage erişilemezse sessizce devam et
    }
  }, [scale])

  const setScale = React.useCallback<DisplayScaleContextValue['setScale']>((nextScale) => {
    setScaleState(nextScale)
  }, [])

  const resetScale = React.useCallback(() => {
    setScaleState(DEFAULT_SCALE)
  }, [])

  const value = React.useMemo(
    () => ({
      scale,
      setScale,
      resetScale,
    }),
    [scale, setScale, resetScale],
  )

  return <DisplayScaleContext.Provider value={value}>{children}</DisplayScaleContext.Provider>
}
